import { sortBy } from "lodash";
import {
  SkillBuilder_ConditionalStepGroupFragment,
  SkillBuilder_SkillVersionFragment,
  SkillBuilder_TriggerFragment,
  SkillBuilder_VerificationStepFragment,
} from "../SkillDetailPage.generated";
import type { SkillActionPayload } from "../hooks/useDispatchSkillBuilderAction";
import { triggerToTriggerInput } from "./triggers";
import NumberIcon from "@src/lib/icons/verificationStepTypes/number";
import TemperatureIcon from "@src/lib/icons/verificationStepTypes/temperature";
import MultipleChoiceIcon from "@src/lib/icons/promptTypes/multiple-choice";
import CheckboxIcon from "@src/lib/icons/verificationStepTypes/checkbox";
import TrueFalseIcon from "@src/lib/icons/promptTypes/true-false";
import RatingIcon from "@src/lib/icons/verificationStepTypes/rating";
import CopiesPlusIcon from "@src/lib/icons/18px/copies-plus";
import PromptTypeShortResponseIcon from "@src/lib/icons/promptTypes/short-response";
import {
  AddConditionalStepGroupActionInput,
  AddStepActionInput,
  ConditionalStepGroupInput,
  LibraryItemType,
  NumberTriggerCondition,
  StepResponseOptionObjectInput,
  TemperatureUnit,
  TriggerInput,
  VerificationStepObjectInput,
  VerificationStepType,
} from "@src/types.generated";
import {
  stripHtml,
  stripHtmlSpecialCharacters,
  uuid4,
} from "@src/lib/utils/strings";
import {
  ConditionFields,
  NUMBER_TRIGGER_CONDITION_COPY,
} from "../SelectCondition";
import { recursivelyReplaceIdsAndUuidsWithNewUuids } from "../../course/utils/utils";

export const skillDescriptionEditorId = (skillId?: number): string => {
  return `skill-description-${skillId}`;
};

export const getSkillNameEditorId = (skillId: number | undefined): string =>
  `skill-name-editor-${skillId}`;

export const getStep = <T extends SkillBuilder_SkillVersionFragment>(
  stepId: string,
  skillVersion: T,
): T["verificationSteps"][0] | undefined => {
  return skillVersion.verificationSteps.find((step) => step.id === stepId);
};

export const getStepFromStepResponseOptionId = <
  T extends SkillBuilder_SkillVersionFragment,
>(
  stepResponseOptionId: string,
  skillVersion: T,
): T["verificationSteps"][0] | undefined => {
  return skillVersion.verificationSteps.find((step) =>
    step.responseOptions.find((option) => option.id === stepResponseOptionId),
  );
};

export const getAttemptNumOfAttemptsCount = (si: {
  id: string;
  assignment?: {
    skillInstances: {
      id: string;
      verifiedAt?: string | null | undefined;
    }[];
  } | null;
}): string => {
  const verifiedSkillInstances =
    si.assignment?.skillInstances.filter((a_si) => a_si.verifiedAt) || [];
  const sortedSkillInstances = sortBy(verifiedSkillInstances, "verifiedAt");
  const attemptNum = sortedSkillInstances.findIndex((s) => s.id === si.id) + 1;
  return attemptNum ? `${attemptNum} of ${verifiedSkillInstances.length}` : "-";
};

export const ALLOWED_CONDITIONAL_STEP_TYPES = [
  VerificationStepType.Text,
  VerificationStepType.YesNo,
  VerificationStepType.Checkbox,
  VerificationStepType.Number,
  VerificationStepType.Temperature,
  VerificationStepType.MultipleChoice,
];

export const ALLOWED_AUDIT_CONDITIONAL_STEP_TYPES = [
  VerificationStepType.MultipleChoice,
  VerificationStepType.Text,
];

export const STEP_TYPE_TO_ICON = {
  [VerificationStepType.PassFail]: TrueFalseIcon,
  [VerificationStepType.Rating]: RatingIcon,
  [VerificationStepType.YesNo]: TrueFalseIcon,
  [VerificationStepType.Checkbox]: CheckboxIcon,
  [VerificationStepType.Number]: NumberIcon,
  [VerificationStepType.Temperature]: TemperatureIcon,
  [VerificationStepType.MultipleChoice]: MultipleChoiceIcon,
  [VerificationStepType.Text]: PromptTypeShortResponseIcon,
  [VerificationStepType.SectionBreak]: CopiesPlusIcon,
};

export const TEMPERATURE_UNIT = {
  [TemperatureUnit.Celsius]: "°C",
  [TemperatureUnit.Fahrenheit]: "°F",
};

export type NestedStepItem<
  TStep = SkillBuilder_VerificationStepFragment,
  TConditionalGroup = SkillBuilder_ConditionalStepGroupFragment,
> =
  | {
      type: "step";
      step: TStep;
    }
  | {
      type: "conditionalGroup";
      conditionalGroup: TConditionalGroup;
      items: NestedStepItem<TStep, TConditionalGroup>[];
    };

export type NestedStepsSection<
  TStep = SkillBuilder_VerificationStepFragment,
  TConditionalGroup = SkillBuilder_ConditionalStepGroupFragment,
> = {
  sectionStep?: TStep;
  items: NestedStepItem<TStep, TConditionalGroup>[];
};

export type SkillBuilderStepStructureConditionalGroup = {
  id: string;
  parentVerificationStep: { id: string };
};

export type SkillBuilderStepStructure = {
  id: string;
  type: VerificationStepType;
  parentConditionalStepGroup?: SkillBuilderStepStructureConditionalGroup | null;
  childConditionalStepGroups: SkillBuilderStepStructureConditionalGroup[];
};

export const convertVerificationStepFragmentToInput = (
  stepFragment: SkillBuilder_VerificationStepFragment,
): VerificationStepObjectInput => {
  return {
    id: stepFragment.id,
    familyId: stepFragment.familyId,
    type: stepFragment.type,
    text: stepFragment.text
      ? {
          en: stepFragment.text.en || "",
          uuid: stepFragment.text.uuid,
          translationOverrides: stepFragment.text.translationOverrides || [],
        }
      : undefined,
    isRequired: stepFragment.isRequired,
    photoRequired: stepFragment.photoRequired,
    noteRequired: stepFragment.noteRequired,
    videoRequired: stepFragment.videoRequired,
    disableCameraRoll: stepFragment.disableCameraRoll,
    temperatureUnit: stepFragment.temperatureUnit,
    responseOptions: stepFragment.responseOptions?.map((option) => ({
      id: option.id,
      text: {
        en: option.text?.en || "",
        uuid: option.text?.uuid || uuid4(),
        translationOverrides: option.text?.translationOverrides || [],
      },
      isCorrect: option.isCorrect,
      points: option.points,
    })),
  };
};

// Mirrors the server's ConditionalStepGroup model: it only has condition slots
// for these parent answer types (rating answers, for one, are stored in a
// field no condition reads), so a group under any other parent type can
// neither save nor ever fire.
export const CONDITIONAL_STEP_PARENT_TYPES = [
  VerificationStepType.Checkbox,
  VerificationStepType.MultipleChoice,
  VerificationStepType.Number,
  VerificationStepType.Temperature,
  VerificationStepType.YesNo,
];

export const stepTypeSupportsConditionalSteps = (
  type: VerificationStepType,
): boolean => CONDITIONAL_STEP_PARENT_TYPES.includes(type);

export const createDefaultConditionFields = ({
  verificationStepType,
  stepResponseOptionIds,
}: {
  verificationStepType: VerificationStepType;
  stepResponseOptionIds?: string[];
}): Pick<
  ConditionFields,
  | "numberCondition"
  | "yesNoCondition"
  | "checkedCondition"
  | "verificationStepMultipleChoiceCondition"
> => {
  return {
    checkedCondition:
      verificationStepType === VerificationStepType.Checkbox
        ? { value: true }
        : undefined,
    numberCondition:
      verificationStepType === VerificationStepType.Number ||
      verificationStepType === VerificationStepType.Temperature
        ? {
            condition: NumberTriggerCondition.GreaterThan,
            number: { value: 0 },
          }
        : undefined,
    verificationStepMultipleChoiceCondition:
      verificationStepType === VerificationStepType.MultipleChoice
        ? {
            stepResponseOptionId: stepResponseOptionIds?.[0] || "",
            condition: true,
          }
        : undefined,
    yesNoCondition:
      verificationStepType === VerificationStepType.YesNo
        ? { value: true }
        : undefined,
  };
};

export const createNewBlankStep = (
  libraryItemType: LibraryItemType,
  stepType: VerificationStepType,
): VerificationStepObjectInput => {
  const emptyStepResponseOptions = (): StepResponseOptionObjectInput[] => {
    return [
      {
        id: uuid4(),
        text: {
          en: "",
          uuid: uuid4(),
          translationOverrides: [],
        },
        isCorrect: true,
      },
      {
        id: uuid4(),
        text: {
          en: "",
          uuid: uuid4(),
          translationOverrides: [],
        },
        isCorrect: false,
      },
    ];
  };

  const emptyAuditStepResponseOptions = (): StepResponseOptionObjectInput[] => {
    return emptyStepResponseOptions().map((x, index) => ({
      ...x,
      points: index === 0 ? 1 : 0,
    }));
  };

  return {
    id: uuid4(),
    familyId: uuid4(),
    type: stepType,
    isRequired: false,
    responseOptions:
      stepType === VerificationStepType.MultipleChoice
        ? libraryItemType === LibraryItemType.Audit
          ? emptyAuditStepResponseOptions()
          : emptyStepResponseOptions()
        : undefined,
  };
};

export const getAncestorConditionalStepGroupIds = (
  step: SkillBuilder_VerificationStepFragment,
  allSteps: SkillBuilder_VerificationStepFragment[],
): string[] => {
  const ancestorGroupIds: string[] = [];
  let currentStep: SkillBuilder_VerificationStepFragment | undefined = step;

  while (currentStep?.parentConditionalStepGroup) {
    ancestorGroupIds.push(currentStep.parentConditionalStepGroup.id);
    const parentStepId: string =
      currentStep.parentConditionalStepGroup.parentVerificationStep.id;
    currentStep = allSteps.find((s) => s.id === parentStepId);
  }

  return ancestorGroupIds;
};

export const getParentSectionStepId = (
  step: SkillBuilder_VerificationStepFragment,
  allSteps: SkillBuilder_VerificationStepFragment[],
): string | undefined => {
  let topLevelStep: SkillBuilder_VerificationStepFragment | undefined = step;
  while (topLevelStep?.parentConditionalStepGroup) {
    const parentStepId: string =
      topLevelStep.parentConditionalStepGroup.parentVerificationStep.id;
    topLevelStep = allSteps.find((s) => s.id === parentStepId);
  }

  if (!topLevelStep) return undefined;

  const topLevelStepIndex = allSteps.findIndex(
    (s) => s.id === topLevelStep!.id,
  );
  if (topLevelStepIndex === -1) return undefined;

  for (let i = topLevelStepIndex - 1; i >= 0; i--) {
    if (allSteps[i].type === VerificationStepType.SectionBreak) {
      return allSteps[i].id;
    }
  }

  return undefined;
};

export const getAvailableStepsForConditional = (
  allSteps: SkillBuilder_VerificationStepFragment[],
  parentStep: SkillBuilder_VerificationStepFragment,
): SkillBuilder_VerificationStepFragment[] => {
  const ancestorStepIds = new Set<string>();
  let currentStep: SkillBuilder_VerificationStepFragment | undefined =
    parentStep;

  while (currentStep?.parentConditionalStepGroup) {
    const parentVerificationStepId: string =
      currentStep.parentConditionalStepGroup.parentVerificationStep.id;
    ancestorStepIds.add(parentVerificationStepId);
    currentStep = allSteps.find((s) => s.id === parentVerificationStepId);
  }

  return allSteps.filter((step) => {
    if (step.id === parentStep.id) return false;
    if (ancestorStepIds.has(step.id)) return false;
    // Selecting an existing step MOVES it, and moving the last step out of a
    // conditional deletes that conditional (see
    // buildAddConditionalStepGroupActions) — so offering a step that already
    // lives in another conditional silently destroys the builder's earlier
    // work. Only root-level steps can be moved in here.
    if (step.parentConditionalStepGroup) return false;
    if (!ALLOWED_CONDITIONAL_STEP_TYPES.includes(step.type)) return false;
    return true;
  });
};

export const getAllConditionalChildStepsRecursive = (
  parentStepId: string,
  allSteps: SkillBuilder_VerificationStepFragment[],
): SkillBuilder_VerificationStepFragment[] => {
  const directChildren = allSteps.filter(
    (step) =>
      step.parentConditionalStepGroup?.parentVerificationStep.id ===
      parentStepId,
  );

  const allChildren: SkillBuilder_VerificationStepFragment[] = [
    ...directChildren,
  ];

  directChildren.forEach((child) => {
    const nestedChildren = getAllConditionalChildStepsRecursive(
      child.id,
      allSteps,
    );
    allChildren.push(...nestedChildren);
  });

  return allChildren;
};

export type GroupedStepsBySection = {
  sectionName: string | null;
  sectionId: string | null;
  steps: SkillBuilder_VerificationStepFragment[];
};

export const groupStepsBySection = (
  allSteps: SkillBuilder_VerificationStepFragment[],
  availableStepIds?: Set<string>,
): GroupedStepsBySection[] => {
  const groups: GroupedStepsBySection[] = [];

  let currentGroup: GroupedStepsBySection = {
    sectionName: null,
    sectionId: null,
    steps: [],
  };

  allSteps.forEach((s) => {
    if (s.type === VerificationStepType.SectionBreak) {
      if (currentGroup.steps.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = {
        sectionName:
          stripHtmlSpecialCharacters(stripHtml(s.text?.en || "")) || "Section",
        sectionId: s.id,
        steps: [],
      };
    } else if (!availableStepIds || availableStepIds.has(s.id)) {
      currentGroup.steps.push(s);
    }
  });

  if (currentGroup.steps.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
};

export const createConditionalStepTypeOptions = (
  libraryItemType: LibraryItemType,
): VerificationStepType[] => {
  if (libraryItemType === LibraryItemType.Audit) {
    return ALLOWED_AUDIT_CONDITIONAL_STEP_TYPES;
  }

  return ALLOWED_CONDITIONAL_STEP_TYPES;
};

export const findExistingConditionalStepGroupWithSameConditions = (
  parentStep: SkillBuilder_VerificationStepFragment,
  newConditionalStepGroupInput: ConditionalStepGroupInput,
): string | null => {
  const existingGroup = parentStep.childConditionalStepGroups.find(
    (existingGroup) => {
      const existingGroupInput =
        convertConditionalStepGroupObjectToInput(existingGroup);
      return hasSameConditions(
        existingGroupInput,
        newConditionalStepGroupInput,
      );
    },
  );

  return existingGroup?.id || null;
};

export const convertConditionalStepGroupObjectToInput = (
  conditionalStepGroup: SkillBuilder_ConditionalStepGroupFragment,
): ConditionalStepGroupInput => {
  return {
    id: conditionalStepGroup.id,
    numberCondition: conditionalStepGroup.numberCondition
      ? {
          condition: conditionalStepGroup.numberCondition.condition,
          number: { value: conditionalStepGroup.numberCondition.number },
          numberRangeTop:
            conditionalStepGroup.numberCondition.numberRangeTop != null
              ? {
                  value: conditionalStepGroup.numberCondition.numberRangeTop,
                }
              : undefined,
        }
      : undefined,
    yesNoCondition: conditionalStepGroup.yesNoCondition
      ? { value: conditionalStepGroup.yesNoCondition.yesNo }
      : undefined,
    checkedCondition: conditionalStepGroup.checkedCondition
      ? { value: conditionalStepGroup.checkedCondition.checked }
      : undefined,
    verificationStepMultipleChoiceCondition:
      conditionalStepGroup.verificationStepMultipleChoiceCondition
        ? {
            stepResponseOptionId:
              conditionalStepGroup.verificationStepMultipleChoiceCondition
                .stepResponseOption.id,
            condition:
              conditionalStepGroup.verificationStepMultipleChoiceCondition
                .condition,
          }
        : undefined,
  };
};

export const hasSameConditions = (
  condition1: ConditionalStepGroupInput,
  condition2: ConditionalStepGroupInput,
): boolean => {
  const condition1HasYesNo = !!condition1.yesNoCondition;
  const condition2HasYesNo = !!condition2.yesNoCondition;
  const condition1HasChecked = !!condition1.checkedCondition;
  const condition2HasChecked = !!condition2.checkedCondition;
  const condition1HasNumber = !!condition1.numberCondition;
  const condition2HasNumber = !!condition2.numberCondition;
  const condition1HasMultipleChoice =
    !!condition1.verificationStepMultipleChoiceCondition;
  const condition2HasMultipleChoice =
    !!condition2.verificationStepMultipleChoiceCondition;

  if (condition1HasYesNo !== condition2HasYesNo) return false;
  if (condition1HasChecked !== condition2HasChecked) return false;
  if (condition1HasNumber !== condition2HasNumber) return false;
  if (condition1HasMultipleChoice !== condition2HasMultipleChoice) return false;

  if (
    condition1HasYesNo &&
    condition2HasYesNo &&
    condition1.yesNoCondition!.value !== condition2.yesNoCondition!.value
  ) {
    return false;
  }

  if (
    condition1HasChecked &&
    condition2HasChecked &&
    condition1.checkedCondition!.value !== condition2.checkedCondition!.value
  ) {
    return false;
  }

  if (condition1HasNumber && condition2HasNumber) {
    const nc1 = condition1.numberCondition!;
    const nc2 = condition2.numberCondition!;
    if (
      nc1.condition !== nc2.condition ||
      nc1.number.value !== nc2.number.value ||
      nc1.numberRangeTop?.value !== nc2.numberRangeTop?.value
    ) {
      return false;
    }
  }

  if (condition1HasMultipleChoice && condition2HasMultipleChoice) {
    const mc1 = condition1.verificationStepMultipleChoiceCondition!;
    const mc2 = condition2.verificationStepMultipleChoiceCondition!;
    if (
      mc1.stepResponseOptionId !== mc2.stepResponseOptionId ||
      mc1.condition !== mc2.condition
    ) {
      return false;
    }
  }

  return true;
};

export const getDescendantStepsForConditionalStepGroup = (
  conditionalStepGroupId: string,
  allSteps: SkillBuilder_VerificationStepFragment[],
): SkillBuilder_VerificationStepFragment[] => {
  const directChildren = allSteps.filter(
    (step) => step.parentConditionalStepGroup?.id === conditionalStepGroupId,
  );

  const allDescendants: SkillBuilder_VerificationStepFragment[] = [
    ...directChildren,
  ];

  directChildren.forEach((child) => {
    child.childConditionalStepGroups.forEach((nestedCsg) => {
      const nestedDescendants = getDescendantStepsForConditionalStepGroup(
        nestedCsg.id,
        allSteps,
      );
      allDescendants.push(...nestedDescendants);
    });
  });

  return allDescendants;
};

export const getAllDescendantStepsForConditionalStepGroupIds = (
  conditionalStepGroupIds: string[],
  allSteps: SkillBuilder_VerificationStepFragment[],
): SkillBuilder_VerificationStepFragment[] => {
  const allDescendants: SkillBuilder_VerificationStepFragment[] = [];
  conditionalStepGroupIds.forEach((groupId) => {
    const groupDescendants = getDescendantStepsForConditionalStepGroup(
      groupId,
      allSteps,
    );
    allDescendants.push(...groupDescendants);
  });
  return allDescendants;
};

export const addStepAction = (
  step: VerificationStepObjectInput,
  position: number,
  parentConditionalStepGroupId?: string,
): SkillActionPayload => {
  return {
    action: {
      payload: {
        addStepAction: {
          position,
          step: {
            ...step,
            ...(parentConditionalStepGroupId && {
              parentConditionalStepGroupId,
            }),
          },
        },
      },
    },
    inverseAction: {
      payload: {
        removeStepAction: {
          id: step.id,
        },
      },
    },
  };
};

export const removeStepAction = (
  step: SkillBuilder_VerificationStepFragment,
  allSteps: SkillBuilder_VerificationStepFragment[],
): SkillActionPayload => {
  const stepInput = convertVerificationStepFragmentToInput(step);
  const parentGroupId = step.parentConditionalStepGroup?.id;
  let siblingPosition: number;
  if (parentGroupId) {
    const siblings = allSteps.filter(
      (s) => s.parentConditionalStepGroup?.id === parentGroupId,
    );
    siblingPosition = siblings.findIndex((s) => s.id === step.id);
  } else {
    const rootSteps = allSteps.filter((s) => !s.parentConditionalStepGroup);
    siblingPosition = rootSteps.findIndex((s) => s.id === step.id);
  }

  return {
    action: {
      payload: {
        removeStepAction: {
          id: step.id,
        },
      },
    },
    inverseAction: {
      payload: {
        addStepAction: {
          step: {
            ...stepInput,
            parentConditionalStepGroupId: parentGroupId,
          },
          position: siblingPosition,
        },
      },
    },
  };
};

export const calculateConditionalGroupInsertPosition = (
  steps: SkillBuilder_VerificationStepFragment[],
  parentConditionalStepGroupId: string,
  position: number,
): number => {
  const parentStep = steps.find((s) =>
    s.childConditionalStepGroups.some(
      (group) => group.id === parentConditionalStepGroupId,
    ),
  );
  if (!parentStep) {
    throw new Error(
      `Parent step not found for conditional group ${parentConditionalStepGroupId}`,
    );
  }

  const groupStepIndices: number[] = [];
  steps.forEach((s, index) => {
    if (s.parentConditionalStepGroup?.id === parentConditionalStepGroupId) {
      groupStepIndices.push(index);
    }
  });

  if (groupStepIndices.length === 0) {
    return steps.findIndex((s) => s.id === parentStep.id) + 1;
  } else if (position >= groupStepIndices.length) {
    return groupStepIndices[groupStepIndices.length - 1] + 1;
  } else {
    return groupStepIndices[position];
  }
};

export const calculateRootLevelInsertPosition = (
  steps: SkillBuilder_VerificationStepFragment[],
  position: number,
): number => {
  const rootStepIndices: number[] = [];
  steps.forEach((s, index) => {
    if (!s.parentConditionalStepGroup) {
      rootStepIndices.push(index);
    }
  });

  if (rootStepIndices.length === 0) {
    return 0;
  } else if (position >= rootStepIndices.length) {
    return rootStepIndices[rootStepIndices.length - 1] + 1;
  } else {
    return rootStepIndices[position];
  }
};

export const moveStepAction = (
  step: SkillBuilder_VerificationStepFragment,
  newPosition: number,
  allSteps: SkillBuilder_VerificationStepFragment[],
  newParentConditionalStepGroupId?: string | null,
): SkillActionPayload => {
  const originalPosition = allSteps.findIndex((s) => s.id === step.id);
  const parentGroupId = step.parentConditionalStepGroup?.id;

  return {
    action: {
      payload: {
        moveStepAction: {
          id: step.id,
          position: newPosition,
          parentConditionalStepGroupId: newParentConditionalStepGroupId,
        },
      },
    },
    inverseAction: {
      payload: {
        moveStepAction: {
          id: step.id,
          position: originalPosition,
          parentConditionalStepGroupId: parentGroupId,
        },
      },
    },
  };
};

export const addConditionalStepGroupAction = (
  group: ConditionalStepGroupInput,
  parentStepId: string,
): SkillActionPayload => {
  return {
    action: {
      payload: {
        addConditionalStepGroupAction: {
          conditionalStepGroup: group,
          parentVerificationStepId: parentStepId,
        },
      },
    },
    inverseAction: {
      payload: {
        removeConditionalStepGroupAction: {
          conditionalStepGroupId: group.id,
          deleteDescendantSteps: true,
        },
      },
    },
  };
};

export const removeConditionalStepGroupAction = (
  group: SkillBuilder_ConditionalStepGroupFragment,
  parentStepId: string,
  deleteDescendantSteps: boolean,
): SkillActionPayload => {
  const groupInput = convertConditionalStepGroupObjectToInput(group);

  return {
    action: {
      payload: {
        removeConditionalStepGroupAction: {
          conditionalStepGroupId: group.id,
          deleteDescendantSteps,
        },
      },
    },
    inverseAction: {
      payload: {
        addConditionalStepGroupAction: {
          conditionalStepGroup: groupInput,
          parentVerificationStepId: parentStepId,
        },
      },
    },
  };
};

export const buildAddConditionalStepGroupActions = (args: {
  addConditionalStepGroupActionInput: AddConditionalStepGroupActionInput;
  addStepActionInput: AddStepActionInput;
  skillVersion: SkillBuilder_SkillVersionFragment;
}): SkillActionPayload[] => {
  const {
    addConditionalStepGroupActionInput,
    addStepActionInput,
    skillVersion,
  } = args;

  const parentStep = getStep(
    addConditionalStepGroupActionInput.parentVerificationStepId,
    skillVersion,
  );
  if (!parentStep) return [];

  const existingConditionalStepGroupId =
    findExistingConditionalStepGroupWithSameConditions(
      parentStep,
      addConditionalStepGroupActionInput.conditionalStepGroup,
    );

  const conditionalStepGroupId =
    existingConditionalStepGroupId ||
    addConditionalStepGroupActionInput.conditionalStepGroup.id;

  const steps = skillVersion.verificationSteps;
  const existingStep = steps.find(
    (step) => step.id === addStepActionInput.step.id,
  );

  const actions: SkillActionPayload[] = [];

  if (!existingConditionalStepGroupId) {
    actions.push(
      addConditionalStepGroupAction(
        addConditionalStepGroupActionInput.conditionalStepGroup,
        addConditionalStepGroupActionInput.parentVerificationStepId,
      ),
    );
  }

  if (existingStep) {
    const stepsInGroup = steps.filter(
      (s) => s.parentConditionalStepGroup?.id === conditionalStepGroupId,
    );
    actions.push(
      moveStepAction(
        existingStep,
        stepsInGroup.length,
        steps,
        conditionalStepGroupId,
      ),
    );

    const sourceGroupId = existingStep.parentConditionalStepGroup?.id;
    if (sourceGroupId && sourceGroupId !== conditionalStepGroupId) {
      const stepsInSourceGroup = steps.filter(
        (s) => s.parentConditionalStepGroup?.id === sourceGroupId,
      );
      if (stepsInSourceGroup.length === 1) {
        const sourceParentStep = steps.find((s) =>
          s.childConditionalStepGroups.some((g) => g.id === sourceGroupId),
        );
        const sourceGroup = sourceParentStep?.childConditionalStepGroups.find(
          (g) => g.id === sourceGroupId,
        );
        if (sourceParentStep && sourceGroup) {
          actions.push(
            removeConditionalStepGroupAction(
              sourceGroup,
              sourceParentStep.id,
              false,
            ),
          );
        }
      }
    }
  } else {
    actions.push(
      addStepAction(
        addStepActionInput.step,
        addStepActionInput.position,
        conditionalStepGroupId,
      ),
    );
  }

  return actions;
};

export const setStepMediaAction = (
  stepId: string,
  mediaId: number | undefined,
): SkillActionPayload => {
  return {
    action: {
      payload: {
        setStepMediaAction: { stepId, mediaId },
      },
    },
    inverseAction: {
      payload: {
        setStepMediaAction: { stepId, mediaId: undefined },
      },
    },
  };
};

export const setStepLibraryItemLinkAction = (
  stepId: string,
  libraryItemId: string | undefined,
): SkillActionPayload => {
  return {
    action: {
      payload: {
        setStepLibraryItemLinkAction: { stepId, libraryItemId },
      },
    },
    inverseAction: {
      payload: {
        setStepLibraryItemLinkAction: { stepId, libraryItemId: undefined },
      },
    },
  };
};

export const setTriggerAction = (
  verificationStepId: string,
  trigger: TriggerInput | null,
): SkillActionPayload => {
  return {
    action: {
      payload: {
        setVerificationStepTriggerAction: {
          verificationStepId,
          trigger,
        },
      },
    },
    inverseAction: {
      payload: {
        setVerificationStepTriggerAction: {
          verificationStepId,
          trigger: null,
        },
      },
    },
  };
};

export const getAllNestedConditionalStepGroups = (
  descendantSteps: SkillBuilder_VerificationStepFragment[],
  allSteps: SkillBuilder_VerificationStepFragment[],
): Map<string, SkillBuilder_VerificationStepFragment[]> => {
  const nestedGroupsMap = new Map<
    string,
    SkillBuilder_VerificationStepFragment[]
  >();

  const processNestedGroups = (
    steps: SkillBuilder_VerificationStepFragment[],
  ) => {
    steps.forEach((step) => {
      step.childConditionalStepGroups.forEach((group) => {
        const groupSteps = getDescendantStepsForConditionalStepGroup(
          group.id,
          allSteps,
        );
        nestedGroupsMap.set(group.id, groupSteps);
        processNestedGroups(groupSteps);
      });
    });
  };

  processNestedGroups(descendantSteps);
  return nestedGroupsMap;
};

export const buildNestedStepsSections = <
  TStep extends {
    id: string;
    type: VerificationStepType;
    parentConditionalStepGroup?: { id: string } | null;
    childConditionalStepGroups: Array<{ id: string }>;
  },
>(
  allSteps: TStep[],
): NestedStepsSection<TStep, TStep["childConditionalStepGroups"][number]>[] => {
  const filteredSteps = allSteps.filter(
    (step) => step.type !== VerificationStepType.PassFail,
  );

  const sections: NestedStepsSection<
    TStep,
    TStep["childConditionalStepGroups"][number]
  >[] = [];
  let currentSectionSteps: TStep[] = [];
  let currentSectionStep: TStep | undefined;

  filteredSteps.forEach((step) => {
    if (step.type === VerificationStepType.SectionBreak) {
      if (currentSectionStep || currentSectionSteps.length > 0) {
        sections.push({
          sectionStep: currentSectionStep,
          items: buildNestedItems(currentSectionSteps, filteredSteps),
        });
      }
      currentSectionStep = step;
      currentSectionSteps = [];
    } else if (!step.parentConditionalStepGroup) {
      currentSectionSteps.push(step);
    }
  });

  if (currentSectionStep || currentSectionSteps.length > 0) {
    sections.push({
      sectionStep: currentSectionStep,
      items: buildNestedItems(currentSectionSteps, filteredSteps),
    });
  }

  return sections;
};

const buildNestedItems = <
  TStep extends {
    id: string;
    parentConditionalStepGroup?: { id: string } | null;
    childConditionalStepGroups: Array<{ id: string }>;
  },
>(
  sectionSteps: TStep[],
  allSteps: TStep[],
): NestedStepItem<TStep, TStep["childConditionalStepGroups"][number]>[] => {
  type TConditionalGroup = TStep["childConditionalStepGroups"][number];
  const stepsByParentGroup = new Map<string, TStep[]>();
  const conditionalGroupsByParentStep = new Map<string, TConditionalGroup[]>();

  allSteps.forEach((step) => {
    if (step.parentConditionalStepGroup) {
      const groupId = step.parentConditionalStepGroup.id;
      if (!stepsByParentGroup.has(groupId)) {
        stepsByParentGroup.set(groupId, []);
      }
      stepsByParentGroup.get(groupId)!.push(step);
    }
  });

  allSteps.forEach((step) => {
    if (step.childConditionalStepGroups.length > 0) {
      conditionalGroupsByParentStep.set(
        step.id,
        step.childConditionalStepGroups,
      );
    }
  });

  const rootSteps = sectionSteps.filter(
    (step) => !step.parentConditionalStepGroup,
  );
  const items: NestedStepItem<TStep, TConditionalGroup>[] = [];

  const processStep = (
    step: TStep,
  ): NestedStepItem<TStep, TConditionalGroup>[] => {
    const stepItems: NestedStepItem<TStep, TConditionalGroup>[] = [];

    stepItems.push({ type: "step", step });

    const childGroups = conditionalGroupsByParentStep.get(step.id) || [];
    if (childGroups.length > 0) {
      childGroups.forEach((group) => {
        const groupSteps = (stepsByParentGroup.get(group.id) || []).sort(
          (a, b) => {
            const indexA = allSteps.findIndex((s) => s.id === a.id);
            const indexB = allSteps.findIndex((s) => s.id === b.id);
            return indexA - indexB;
          },
        );

        const nestedItems: NestedStepItem<TStep, TConditionalGroup>[] = [];
        groupSteps.forEach((groupStep) => {
          nestedItems.push(...processStep(groupStep));
        });

        stepItems.push({
          type: "conditionalGroup",
          conditionalGroup: group,
          items: nestedItems,
        });
      });
    }

    return stepItems;
  };

  rootSteps.forEach((step) => {
    items.push(...processStep(step));
  });

  return items;
};

export const getAllStepsInNestedConditionalGroups = (
  steps: SkillBuilder_VerificationStepFragment[],
  allSteps: SkillBuilder_VerificationStepFragment[],
): SkillBuilder_VerificationStepFragment[] => {
  const nestedSteps: SkillBuilder_VerificationStepFragment[] = [];
  const visited = new Set<string>();

  const collectNestedSteps = (step: SkillBuilder_VerificationStepFragment) => {
    step.childConditionalStepGroups.forEach((childGroup) => {
      const nestedGroupSteps = getStepsInConditionalGroup(
        childGroup.id,
        allSteps,
      );
      nestedGroupSteps.forEach((nestedStep) => {
        if (!visited.has(nestedStep.id)) {
          visited.add(nestedStep.id);
          nestedSteps.push(nestedStep);
          collectNestedSteps(nestedStep);
        }
      });
    });
  };

  steps.forEach((step) => {
    collectNestedSteps(step);
  });

  return nestedSteps;
};

export const getDirectStepsInConditionalGroup = (
  groupId: string,
  allSteps: SkillBuilder_VerificationStepFragment[],
): SkillBuilder_VerificationStepFragment[] => {
  return allSteps.filter(
    (step) => step.parentConditionalStepGroup?.id === groupId,
  );
};

export const getStepsInConditionalGroup = (
  groupId: string,
  allSteps: SkillBuilder_VerificationStepFragment[],
): SkillBuilder_VerificationStepFragment[] => {
  const groupSteps: SkillBuilder_VerificationStepFragment[] = [];
  const visited = new Set<string>();

  const collectSteps = (currentGroupId: string) => {
    const directSteps = allSteps.filter(
      (step) => step.parentConditionalStepGroup?.id === currentGroupId,
    );

    directSteps.forEach((step) => {
      if (!visited.has(step.id)) {
        visited.add(step.id);
        groupSteps.push(step);

        step.childConditionalStepGroups.forEach((childGroup) => {
          collectSteps(childGroup.id);
        });
      }
    });
  };

  collectSteps(groupId);
  return groupSteps;
};

export const getAllStepsFromNestedItems = <TStep, TConditionalGroup>(
  items: NestedStepItem<TStep, TConditionalGroup>[],
): TStep[] => {
  const allSteps: TStep[] = [];
  items.forEach((item) => {
    if (item.type === "step") {
      allSteps.push(item.step);
    } else if (item.type === "conditionalGroup") {
      allSteps.push(...getAllStepsFromNestedItems(item.items));
    }
  });
  return allSteps;
};

export const getMatchingSteps = <
  TStep extends { id: string },
  TConditionalGroup,
>(
  items: NestedStepItem<TStep, TConditionalGroup>[],
  stepMatches: Set<string>,
): TStep[] => {
  const matchingSteps: TStep[] = [];

  items.forEach((item) => {
    if (item.type === "step") {
      if (stepMatches.has(item.step.id)) {
        matchingSteps.push(item.step);
      }
    } else if (item.type === "conditionalGroup") {
      matchingSteps.push(...getMatchingSteps(item.items, stepMatches));
    }
  });

  return matchingSteps;
};

export type DropTargetInfo = {
  position: number;
  parentConditionalStepGroupId: string | null;
};

// Convert a sibling-relative position (within the given parent group, or root
// if null) into an absolute index in `allSteps`. The dispatcher expects
// absolute positions; spacer droppables emit sibling-relative ones, so they
// must be translated here. If the sibling position is past the last sibling,
// insert immediately after the last sibling's absolute slot.
const siblingPositionToAbsolute = <
  TStep extends {
    id: string;
    parentConditionalStepGroup?: { id: string } | null;
  },
>(
  allSteps: TStep[],
  parentConditionalStepGroupId: string | null,
  siblingPosition: number,
): number => {
  const siblingIndices: number[] = [];
  allSteps.forEach((s, idx) => {
    const sParent = s.parentConditionalStepGroup?.id ?? null;
    if (sParent === parentConditionalStepGroupId) {
      siblingIndices.push(idx);
    }
  });
  if (siblingPosition >= siblingIndices.length) {
    const last = siblingIndices[siblingIndices.length - 1];
    return last !== undefined ? last + 1 : allSteps.length;
  }
  return siblingIndices[siblingPosition] ?? allSteps.length;
};

export const findDropTargetInfo = <
  TStep extends {
    id: string;
    parentConditionalStepGroup?: { id: string } | null;
    childConditionalStepGroups: Array<{ id: string }>;
  },
>(
  targetId: string,
  skillSections: NestedStepsSection<
    TStep,
    TStep["childConditionalStepGroups"][number]
  >[],
  allSteps: TStep[],
): DropTargetInfo | null => {
  const targetIdStr = targetId.toString();

  if (targetIdStr.startsWith("section-end-spacer-pos-")) {
    const siblingPosition = parseInt(
      targetIdStr.replace("section-end-spacer-pos-", ""),
      10,
    );
    return {
      position: siblingPositionToAbsolute(allSteps, null, siblingPosition),
      parentConditionalStepGroupId: null,
    };
  }

  if (targetIdStr.startsWith("step-spacer-")) {
    if (targetIdStr.startsWith("step-spacer-group-")) {
      const rest = targetIdStr.replace("step-spacer-group-", "");
      const posMarker = "-pos-";
      const posIndex = rest.lastIndexOf(posMarker);
      if (posIndex !== -1) {
        const groupId = rest.slice(0, posIndex);
        const siblingPosition = parseInt(
          rest.slice(posIndex + posMarker.length),
          10,
        );
        if (!Number.isNaN(siblingPosition)) {
          return {
            position: siblingPositionToAbsolute(
              allSteps,
              groupId,
              siblingPosition,
            ),
            parentConditionalStepGroupId: groupId,
          };
        }
      }
    } else if (targetIdStr.startsWith("step-spacer-pos-")) {
      const siblingPosition = parseInt(
        targetIdStr.replace("step-spacer-pos-", ""),
        10,
      );
      return {
        position: siblingPositionToAbsolute(allSteps, null, siblingPosition),
        parentConditionalStepGroupId: null,
      };
    } else {
      const stepId = targetIdStr.replace("step-spacer-", "");
      const targetStep = allSteps.find((step) => step.id === stepId);
      if (targetStep) {
        const position = allSteps.findIndex((s) => s.id === stepId);
        return {
          position,
          parentConditionalStepGroupId:
            targetStep.parentConditionalStepGroup?.id || null,
        };
      }
    }
    return null;
  }

  if (targetIdStr.startsWith("empty-section-")) {
    const sectionId = targetIdStr.replace("empty-section-", "");
    const section = skillSections.find((s) => s.sectionStep?.id === sectionId);
    if (section) {
      return {
        position:
          allSteps.findIndex((x) => x.id === section.sectionStep?.id) + 1,
        parentConditionalStepGroupId: null,
      };
    }
    return null;
  }

  if (targetIdStr.startsWith("section-step-")) {
    const sectionStepId = targetIdStr.replace("section-step-", "");
    const sectionStepPosition = allSteps.findIndex(
      (step) => step.id === sectionStepId,
    );
    if (sectionStepPosition !== -1) {
      return {
        position: sectionStepPosition + 1,
        parentConditionalStepGroupId: null,
      };
    }
    return null;
  }

  if (targetIdStr.startsWith("conditional-group-")) {
    const groupId = targetIdStr.replace("conditional-group-", "");
    for (const section of skillSections) {
      const found = findConditionalGroupInItems(
        section.items,
        groupId,
        allSteps,
      );
      if (found) {
        return found;
      }
    }
    return null;
  }

  const targetStep = allSteps.find((step) => step.id === targetIdStr);
  if (targetStep) {
    const stepIndex = allSteps.findIndex((s) => s.id === targetIdStr);
    return {
      position: stepIndex + 1,
      parentConditionalStepGroupId:
        targetStep.parentConditionalStepGroup?.id || null,
    };
  }

  return null;
};

const findConditionalGroupInItems = <
  TStep extends {
    id: string;
    parentConditionalStepGroup?: { id: string } | null;
    childConditionalStepGroups: Array<{ id: string }>;
  },
  TConditionalGroup extends { id: string },
>(
  items: NestedStepItem<TStep, TConditionalGroup>[],
  groupId: string,
  allSteps: TStep[],
): DropTargetInfo | null => {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (
      item.type === "conditionalGroup" &&
      item.conditionalGroup.id === groupId
    ) {
      const groupSteps = getAllStepsFromNestedItems(item.items);

      if (groupSteps.length > 0) {
        const firstStep = groupSteps[0];
        const position = allSteps.findIndex((s) => s.id === firstStep.id);
        return {
          position,
          parentConditionalStepGroupId: groupId,
        };
      }

      const parentStep = allSteps.find((s) =>
        s.childConditionalStepGroups.some((csg) => csg.id === groupId),
      );

      if (parentStep) {
        const parentPosition = allSteps.findIndex(
          (s) => s.id === parentStep.id,
        );
        let nextPosition = parentPosition + 1;

        while (
          nextPosition < allSteps.length &&
          allSteps[nextPosition].parentConditionalStepGroup?.id === groupId
        ) {
          nextPosition++;
        }

        return {
          position: nextPosition,
          parentConditionalStepGroupId: groupId,
        };
      }
    } else if (item.type === "conditionalGroup") {
      const found = findConditionalGroupInItems(item.items, groupId, allSteps);

      if (found) return found;
    }
  }
  return null;
};

export const getConditionalStepGroupLabel = (
  conditionalStepGroup: SkillBuilder_ConditionalStepGroupFragment,
  parentStep: SkillBuilder_VerificationStepFragment,
): string => {
  let label = "";

  if (conditionalStepGroup.yesNoCondition) {
    const value = conditionalStepGroup.yesNoCondition.yesNo;
    label = value ? "Yes" : "No";
  } else if (conditionalStepGroup.checkedCondition) {
    const value = conditionalStepGroup.checkedCondition.checked;
    label = value ? "checked" : "unchecked";
  } else if (conditionalStepGroup.verificationStepMultipleChoiceCondition) {
    const condition =
      conditionalStepGroup.verificationStepMultipleChoiceCondition;
    const stepResponseOptionId = condition.stepResponseOption.id;
    const option = parentStep.responseOptions?.find(
      (opt) => opt.id === stepResponseOptionId,
    );
    const optionText = option?.text?.en || "";
    const isSelected = condition.condition;
    label = !isSelected ? "not " + optionText : optionText;
  } else if (conditionalStepGroup.numberCondition) {
    const condition = conditionalStepGroup.numberCondition;

    if (parentStep.type === VerificationStepType.Temperature) {
      const unit = parentStep.temperatureUnit
        ? TEMPERATURE_UNIT[parentStep.temperatureUnit]
        : "";
      if (condition.condition === NumberTriggerCondition.OutsideRange) {
        label = `outside ${condition.number}${unit} to ${condition.numberRangeTop}${unit}`;
      } else {
        label = `${NUMBER_TRIGGER_CONDITION_COPY[condition.condition]} ${condition.number}${unit}`;
      }
    } else {
      label = `${NUMBER_TRIGGER_CONDITION_COPY[condition.condition]} ${condition.number}`;
    }
  }

  return label;
};

export const getConditionalStepGroupMinMaxChildStepCount = (
  conditionalStepGroup: SkillBuilder_ConditionalStepGroupFragment,
  allSteps: SkillBuilder_VerificationStepFragment[],
): string => {
  const directSteps = getDirectStepsInConditionalGroup(
    conditionalStepGroup.id,
    allSteps,
  );

  if (directSteps.length === 0) return "";

  const min = directSteps.length;

  const hasNestedConditionalGroups = directSteps.some(
    (step) => step.childConditionalStepGroups.length > 0,
  );

  if (!hasNestedConditionalGroups) {
    const stepText = min === 1 ? "more step" : "more steps";
    return ` show ${min} ${stepText}`;
  }

  const nestedSteps = getAllStepsInNestedConditionalGroups(
    directSteps,
    allSteps,
  );
  const max = min + nestedSteps.length;

  const stepText = max === 1 ? "more step" : "more steps";
  return min === max
    ? ` show ${min} ${stepText}`
    : ` show ${min}-${max} ${stepText}`;
};

export function isConditionValidForParentType(
  stepConditionalStepGroup: SkillBuilder_ConditionalStepGroupFragment,
  parentStep: SkillBuilder_VerificationStepFragment,
): boolean {
  const parentType = parentStep.type;
  if (
    parentType === VerificationStepType.YesNo &&
    !!stepConditionalStepGroup.yesNoCondition
  ) {
    return true;
  }
  if (
    parentType === VerificationStepType.Checkbox &&
    !!stepConditionalStepGroup.checkedCondition
  ) {
    return true;
  }
  if (
    (parentType === VerificationStepType.Number ||
      parentType === VerificationStepType.Temperature) &&
    !!stepConditionalStepGroup.numberCondition
  ) {
    return true;
  }
  if (parentType === VerificationStepType.MultipleChoice) {
    const condition =
      stepConditionalStepGroup.verificationStepMultipleChoiceCondition;
    if (!condition) return false;
    return parentStep.responseOptions.some(
      (option) => option.id === condition.stepResponseOption.id,
    );
  }
  return false;
}

export const isConditionalStepGroupConditionMet = <
  TConditionalStepGroup extends {
    numberCondition?: {
      condition: NumberTriggerCondition;
      number: number;
      numberRangeTop?: number | null;
    } | null;
    yesNoCondition?: { yesNo: boolean } | null;
    checkedCondition?: { checked: boolean } | null;
    verificationStepMultipleChoiceCondition?: {
      condition: boolean;
      stepResponseOption: { id: string };
    } | null;
  },
  TParentStepInstance extends
    | {
        completedAt?: string | null;
        yesNoAnswer?: boolean | null;
        checkboxAnswer?: boolean | null;
        numberAnswer?: number | null;
        temperatureAnswer?: number | null;
        stepResponseOptionInstances: Array<{
          stepResponseOption: { id: string };
        }>;
      }
    | null
    | undefined,
>(
  conditionalStepGroup: TConditionalStepGroup,
  parentStepInstance: TParentStepInstance,
): boolean => {
  // If the parent step hasn't been submitted, we can't evaluate conditions
  if (!parentStepInstance?.completedAt) return false;

  if (conditionalStepGroup.yesNoCondition) {
    return (
      parentStepInstance.yesNoAnswer ===
      conditionalStepGroup.yesNoCondition.yesNo
    );
  }

  if (conditionalStepGroup.checkedCondition) {
    return (
      parentStepInstance.checkboxAnswer ===
      conditionalStepGroup.checkedCondition.checked
    );
  }

  if (conditionalStepGroup.numberCondition) {
    const value =
      parentStepInstance.numberAnswer ?? parentStepInstance.temperatureAnswer;
    if (value == null) return false;

    const { condition, number, numberRangeTop } =
      conditionalStepGroup.numberCondition;

    switch (condition) {
      case NumberTriggerCondition.GreaterThan:
        return value > number;
      case NumberTriggerCondition.LowerThan:
        return value < number;
      case NumberTriggerCondition.EqualTo:
        return value === number;
      case NumberTriggerCondition.NotEqual:
        return value !== number;
      case NumberTriggerCondition.OutsideRange: {
        if (numberRangeTop == null) return false;
        const min = Math.min(number, numberRangeTop);
        const max = Math.max(number, numberRangeTop);
        return value < min || value > max;
      }
      default:
        return false;
    }
  }

  if (conditionalStepGroup.verificationStepMultipleChoiceCondition) {
    const { stepResponseOption, condition } =
      conditionalStepGroup.verificationStepMultipleChoiceCondition;
    const selectedIds = new Set(
      parentStepInstance.stepResponseOptionInstances.map(
        (x) => x.stepResponseOption.id,
      ),
    );

    return condition
      ? selectedIds.has(stepResponseOption.id)
      : !selectedIds.has(stepResponseOption.id);
  }

  return false;
};

export const buildResponseOptionIdMapping = (
  originalOptions: Array<{ id: string }> | null | undefined,
  newOptions: Array<{ id: string }> | null | undefined,
): Map<string, string> => {
  const mapping = new Map<string, string>();
  if (!originalOptions || !newOptions) return mapping;

  originalOptions.forEach((oldOption, index) => {
    const newOption = newOptions[index];
    if (newOption) {
      mapping.set(oldOption.id, newOption.id);
    }
  });

  return mapping;
};

export const duplicateConditionalStepGroup = (
  group: SkillBuilder_ConditionalStepGroupFragment,
  responseOptionIdMapping: Map<string, string>,
): ConditionalStepGroupInput => {
  const groupInput = convertConditionalStepGroupObjectToInput(group);
  const duplicatedGroup = recursivelyReplaceIdsAndUuidsWithNewUuids(groupInput);

  const oldStepResponseOptionId =
    groupInput.verificationStepMultipleChoiceCondition?.stepResponseOptionId;

  if (
    oldStepResponseOptionId &&
    duplicatedGroup.verificationStepMultipleChoiceCondition
  ) {
    const newResponseOptionId = responseOptionIdMapping.get(
      oldStepResponseOptionId,
    );
    if (newResponseOptionId) {
      duplicatedGroup.verificationStepMultipleChoiceCondition.stepResponseOptionId =
        newResponseOptionId;
    }
  }

  return duplicatedGroup;
};

export const duplicateTrigger = (
  trigger: SkillBuilder_TriggerFragment,
  responseOptionIdMapping: Map<string, string>,
): TriggerInput => {
  const triggerInput = triggerToTriggerInput(trigger);

  if (triggerInput.verificationStepMultipleChoiceCondition) {
    const oldId =
      triggerInput.verificationStepMultipleChoiceCondition.stepResponseOptionId;
    const newId = responseOptionIdMapping.get(oldId);
    if (newId) {
      triggerInput.verificationStepMultipleChoiceCondition.stepResponseOptionId =
        newId;
    }
  }

  if (triggerInput.verificationStepMultipleChoiceAnyCondition) {
    triggerInput.verificationStepMultipleChoiceAnyCondition.options =
      triggerInput.verificationStepMultipleChoiceAnyCondition.options.map(
        (opt) => {
          const newId = responseOptionIdMapping.get(opt.stepResponseOptionId);
          return { stepResponseOptionId: newId || opt.stepResponseOptionId };
        },
      );
  }

  return triggerInput;
};

export type AuditScheduleFrequency =
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "semiannually"
  | "annually";

export type AuditScheduleConfig = {
  rrule: string;
};

export const AUDIT_SCHEDULE_FREQUENCIES: {
  value: AuditScheduleFrequency;
  label: string;
  config: AuditScheduleConfig;
}[] = [
  {
    value: "daily",
    label: "Daily",
    config: {
      rrule: "RRULE:FREQ=DAILY",
    },
  },
  {
    value: "weekly",
    label: "Weekly",
    config: {
      rrule: "RRULE:FREQ=WEEKLY;BYDAY=MO",
    },
  },
  {
    value: "monthly",
    label: "Monthly",
    config: {
      rrule: "RRULE:FREQ=MONTHLY;BYMONTHDAY=1",
    },
  },
  {
    value: "quarterly",
    label: "Quarterly",
    config: {
      rrule: "RRULE:FREQ=MONTHLY;INTERVAL=3;BYMONTHDAY=1",
    },
  },
  {
    value: "semiannually",
    label: "Semiannually",
    config: {
      rrule: "RRULE:FREQ=MONTHLY;INTERVAL=6;BYMONTHDAY=1",
    },
  },
  {
    value: "annually",
    label: "Annually",
    config: {
      rrule: "RRULE:FREQ=YEARLY;BYMONTHDAY=1",
    },
  },
];

export const getAuditScheduleConfig = (
  frequency: AuditScheduleFrequency,
): AuditScheduleConfig => {
  const found = AUDIT_SCHEDULE_FREQUENCIES.find((f) => f.value === frequency);
  if (!found) {
    throw new Error(`Unknown frequency: ${frequency}`);
  }
  return found.config;
};

export const getFrequencyFromRRule = (
  rrule: string,
): AuditScheduleFrequency | undefined => {
  const normalizedRRule = rrule.trim().toUpperCase();

  const freqMatch = normalizedRRule.match(/FREQ=(\w+)/);
  const intervalMatch = normalizedRRule.match(/INTERVAL=(\d+)/);

  const freq = freqMatch?.[1];
  const interval = intervalMatch ? parseInt(intervalMatch[1], 10) : 1;

  if (freq === "DAILY") {
    return "daily";
  } else if (freq === "WEEKLY") {
    return "weekly";
  } else if (freq === "MONTHLY") {
    if (interval === 3) {
      return "quarterly";
    } else if (interval === 6) {
      return "semiannually";
    }
    return "monthly";
  } else if (freq === "YEARLY") {
    return "annually";
  }

  return undefined;
};

export const getFrequencyLabelFromRRule = (
  rrule: string,
): string | undefined => {
  const frequency = getFrequencyFromRRule(rrule);

  if (!frequency) return undefined;

  return AUDIT_SCHEDULE_FREQUENCIES.find((f) => f.value === frequency)?.label;
};

export const getQuarterFromMonth = (month: number): number => {
  return Math.ceil(month / 3);
};

export const getHalfFromMonth = (month: number): number => {
  return month <= 6 ? 1 : 2;
};

export const getDateLabelForAuditWindow = (
  windowStart: Date,
  rrule: string,
): string => {
  const frequency = getFrequencyFromRRule(rrule);

  switch (frequency) {
    case "daily":
      return windowStart
        .toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        })
        .replace(",", ""); // Removes first comma
    case "weekly":
      return `Week of ${windowStart.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}`;
    case "monthly":
      return windowStart.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    case "quarterly": {
      const quarter = getQuarterFromMonth(windowStart.getMonth() + 1);
      return `Q${quarter} ${windowStart.getFullYear()}`;
    }
    case "semiannually": {
      const half = getHalfFromMonth(windowStart.getMonth() + 1);
      return `H${half} ${windowStart.getFullYear()}`;
    }
    case "annually":
      return `${windowStart.getFullYear()}`;
    default:
      return windowStart.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
  }
};
